import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { MENU_SECTIONS, locate, sectionHome, type NavSection } from "../lib/nav";
import { TASKS_CHANGED } from "../screens/approvals/Approvals";
import { useAuth, useCrm, useWorkspace } from "../lib/session";
import { useAsync } from "../lib/useAsync";
import { CENTRAL_SUGGESTIONS_PATH, isCentralWorkspace } from "../lib/sourceSuggestions";
import { ChatDrawer } from "./ChatDrawer";
import { AskOgmaContext } from "../lib/askOgma";
import logoOnInk from "../../../../brand/logo/svg/ogma-logo-on-dark.svg";
import markWhite from "../../../../brand/logo/svg/ogma-mark-white.svg";
import type { JsonObject } from "../lib/types";
import { spineBrand } from "../lib/brand";
import { Logo } from "./Logo";
import { OwnerTimeProvider } from "../lib/ownerTimeTracker";
import { FoldPageProvider } from "./kit";
import { SECTION_ICONS } from "./sectionIcons";
import { StepPages, partState, partsFromSetup } from "./module/StepPages";
import { ModuleHeader, moduleEntry } from "./module/ModuleHeader";
import { SpineHealth } from "./SpineHealth";
import { AttentionContext, attention as attentionOf, tabCount } from "../lib/attention";
import { ownerTasks, type OwnerTask } from "../lib/ownerTasks";
import { isEmbedded } from "../lib/embed";

/**
 * The left-hand bar is Ogma's (ink, the Ogma logo for dark grounds) unless the business has chosen white-labelling
 * (settings: app_theme.white_label). Then it takes their colour and their logo, and Ogma shows as "Powered by Ogma".
 */
export function whiteLabelled(config: JsonObject | null | undefined): boolean {
  return spineBrand(config).spine !== null;
}

/** Settings sits at the foot of the spine, under a gap, as in the design. */
const FOOT_SECTIONS = new Set(["settings"]);

export function Layout() {
  const embedded = isEmbedded();
  const { client, session } = useAuth();
  const { tenant } = useWorkspace();
  const crm = useCrm();
  const brand = spineBrand((tenant?.config ?? null) as JsonObject | null);
  const ownLogo = brand.spine !== null;
  const logoUrl = useAsync(() => (ownLogo && brand.logoPath ? crm.brandFileUrl(brand.logoPath) : Promise.resolve(null)), [crm, ownLogo, brand.logoPath]);
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  // A question typed in the Ask bar, handed to the chat drawer once (the id makes asking the same thing twice work).
  const [asked, setAsked] = useState<{ id: number; text: string } | null>(null);
  const [changed, setChanged] = useState(0);
  const tasks = useAsync(() => crm.openHumanTasks(), [crm, location.pathname, changed]);
  // What needs you, counted beside each place and page (Craig, 30 Oct 2026): every task on the page it is resolved on,
  // and your to-dos due by today on Conversations › Next steps (lib/attention.ts).
  const todos = useAsync(() => ownerTasks(crm, crm.email, "today").then((l) => l.tasks).catch(() => [] as OwnerTask[]), [crm, location.pathname, changed]);
  const waiting = useMemo(() => (tasks.data && todos.data ? attentionOf(tasks.data, todos.data) : null), [tasks.data, todos.data]);
  useEffect(() => {
    const on = () => setChanged((n) => n + 1);
    window.addEventListener(TASKS_CHANGED, on);
    return () => window.removeEventListener(TASKS_CHANGED, on);
  }, []);
  // The spine is the only menu: the destinations, with the current one's pages open beneath it. A paused module is
  // not in the menu: its addresses open a Coming soon page.
  const found = locate(location.pathname);
  const at = found?.section.paused ? null : found;
  const here = at?.section.key ?? null;
  // A destination tied to a set-up part (Quotes) shows only while that part is on, or when set-up can't tell.
  const parts = useAsync(() => crm.setupState().then(partsFromSetup, () => null), [crm, tenant?.id]);
  const inMenu = (sec: NavSection) => !sec.shownWithPart || partState(parts.data ?? null, sec.shownWithPart) !== "off";

  useEffect(() => setMenuOpen(false), [location.pathname]);
  const closeChat = useCallback(() => setChatOpen(false), []);
  const ask = useCallback((text: string) => {
    setChatOpen(true);
    if (text) setAsked((a) => ({ id: (a?.id ?? 0) + 1, text }));
  }, []);
  // Ask Ogma has its own page, so no bar sits on top of every page (Craig, 31 Oct 2026); Ctrl+K or ⌘K still opens the
  // assistant beside any page.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        ask("");
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [ask]);

  // The place you are in opens its pages beneath it on the spine: a destination's tabs, and under the current tab its
  // pages when it has several (review of 30 Oct 2026: one vertical menu, no tabs across the top or lists down the side).
  const subNav = (sec: NavSection) => {
    if (!at || at.section.key !== sec.key) return null;
    if (sec.steps) {
      if (sec.steps.length < 2 && (at.step?.items.length ?? 0) < 2) return null;
      return (
        <nav className="spine-sub" aria-label={`${sec.label} tabs`}>
          <ul>
            {sec.steps.map((st) => {
              const current = at.step?.key === st.key;
              const n = waiting ? tabCount(waiting, st.path) : 0;
              return (
                <li key={st.key}>
                  <Link to={st.items[0]!.path} className={`spine-subitem${current ? " is-active" : ""}`} aria-current={current ? "page" : undefined}
                    aria-label={n > 0 ? `${st.label}, ${n} waiting` : undefined}>
                    {st.label}
                    {n > 0 ? <span className="count" aria-hidden="true">{n}</span> : null}
                  </Link>
                  {current && st.items.length > 1 ? (
                    <StepPages label={`${st.label} pages`} items={st.items} current={at.path} module={sec.key} step={st.key} />
                  ) : null}
                </li>
              );
            })}
          </ul>
        </nav>
      );
    }
    if (sec.items.length < 2) return null;
    return (
      <nav className="spine-sub" aria-label={`${sec.label} pages`}>
        <ul>
          {sec.items.map((i) => {
            const current = at.path === i.path;
            const n = waiting?.pages[i.path] ?? 0;
            return (
              <li key={i.path}>
                <Link to={i.path} className={`spine-subitem${current ? " is-active" : ""}`} aria-current={current ? "page" : undefined}
                  aria-label={n > 0 ? `${i.label}, ${n} waiting` : undefined}>
                  {i.label}
                  {n > 0 ? <span className="count" aria-hidden="true">{n}</span> : null}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    );
  };

  const spineLink = (sec: NavSection) => {
    const n = waiting?.places[sec.key] ?? 0;
    const Icon = SECTION_ICONS[sec.key];
    const active = here === sec.key;
    const sub = subNav(sec);
    return (
      <li key={sec.key}>
        <Link
          to={moduleEntry(sec) ?? sectionHome(sec)}
          className={`spine-item${active ? " is-active" : ""}`}
          // With its pages open beneath it, the place is where you are and the page link is the page.
          aria-current={active ? (sub ? "location" : "page") : undefined}
          aria-label={n > 0 ? `${sec.label}, ${n} waiting` : undefined}
        >
          {Icon ? <Icon className="spine-icon" size={18} strokeWidth={1.75} aria-hidden="true" focusable="false" /> : <span className="spine-icon" aria-hidden="true" />}
          <span className="spine-name">{sec.label}</span>
          {n > 0 ? (
            <span className="count" aria-hidden="true">
              {n}
            </span>
          ) : null}
        </Link>
        {sub}
      </li>
    );
  };

  // Ask Ogma's side window shows this page in a frame beside the chat: the page alone, without the menu or the Ask bar.
  if (embedded) {
    return (
      <OwnerTimeProvider pathname={location.pathname} send={(e) => crm.recordOwnerTime(e)}>
        <AskOgmaContext.Provider value={ask}>
          <AttentionContext.Provider value={waiting}>
            <FoldPageProvider path={location.pathname} hash={location.hash}>
              <main id="main" className="main main-embedded" tabIndex={-1}>
                <Outlet />
              </main>
            </FoldPageProvider>
          </AttentionContext.Provider>
        </AskOgmaContext.Provider>
      </OwnerTimeProvider>
    );
  }

  return (
    // E62 owner minutes: active time on each page and task (lib/ownerTimeTracker.tsx)
    <OwnerTimeProvider pathname={location.pathname} send={(e) => crm.recordOwnerTime(e)}>
    {/* Connect package 5: pages ask the same assistant (Sales performance's "Ask about your performance") */}
    <AskOgmaContext.Provider value={ask}>
    <AttentionContext.Provider value={waiting}>
    <div className={`shell${chatOpen ? " shell-chat" : ""}`}>
      <a className="skip" href="#main">
        Skip to content
      </a>
      <header className="topbar">
        <span className="wordmark">
          <img src={markWhite} alt="" className="topbar-mark" width={34} height={16} />
          {tenant?.name ?? "Sales engine"}
        </span>
        <span className="topbar-actions">
          <button type="button" className="btn btn-quiet" onClick={() => setChatOpen(true)} aria-pressed={chatOpen}>
            Assistant
          </button>
          <button type="button" className="btn btn-quiet" aria-expanded={menuOpen} aria-controls="rail" onClick={() => setMenuOpen((o) => !o)}>
            {menuOpen ? "Close" : "Menu"}
            {!menuOpen && waiting?.total ? (
              <span className="count" aria-label={`${waiting.total} waiting for you`}>
                {waiting.total}
              </span>
            ) : null}
          </button>
        </span>
      </header>
      <nav id="rail" className={`rail${menuOpen ? " rail-open" : ""}`} aria-label="Main">
        <div className="rail-brand">
          {!ownLogo && <img src={logoOnInk} alt="Ogma" className="rail-logo" width={150} height={25} />}
          {ownLogo && logoUrl.data ? <img src={logoUrl.data} alt={tenant?.name ?? "Logo"} className="rail-client-logo" /> : <span className="wordmark">{tenant?.name ?? "Sales engine"}</span>}
        </div>
        <div className="spine-sections">
          <ul className="spine-nav">{MENU_SECTIONS.filter((s) => !FOOT_SECTIONS.has(s.key) && inMenu(s)).map(spineLink)}</ul>
          <ul className="spine-nav spine-nav-foot">{MENU_SECTIONS.filter((s) => FOOT_SECTIONS.has(s.key)).map(spineLink)}</ul>
        </div>
        <div className="rail-foot">
          {/* The engine's state shows only when a part needs the owner (UX review, 30 Oct 2026). */}
          <SpineHealth crm={crm} />
          {/* Ogma's team only (L-13, step 2.1): answer the places to look that businesses suggest. */}
          {isCentralWorkspace(tenant) && (
            <Link to={CENTRAL_SUGGESTIONS_PATH} className="rail-ask">
              Source suggestions
            </Link>
          )}
          <p className="rail-user" title={session?.user.email ?? ""}>
            {session?.user.email}
          </p>
          <button type="button" className="link-btn" onClick={() => void client.auth.signOut()}>
            Sign out
          </button>
          {ownLogo && (
            <p className="powered">
              <img src={markWhite} alt="" width={34} height={16} />
              Powered by Ogma
            </p>
          )}
        </div>
      </nav>
      {/* Each card's remembered choice, and the #hash that opens one (kit FoldPage). */}
      <FoldPageProvider path={location.pathname} hash={location.hash}>
      <main id="main" className="main" tabIndex={-1}>
        {at?.section.steps ? <ModuleHeader section={at.section.key} /> : null}
        <Outlet />
      </main>
      </FoldPageProvider>
      <ChatDrawer open={chatOpen} onClose={closeChat} asked={asked} />
    </div>
    </AttentionContext.Provider>
    </AskOgmaContext.Provider>
    </OwnerTimeProvider>
  );
}


/** The frame for someone who has not finished set-up: the business name, the way to set-up, and sign out. */
export function SetupShell() {
  const { client } = useAuth();
  const { tenant } = useWorkspace();
  const crm = useCrm();
  const location = useLocation();
  return (
    <OwnerTimeProvider pathname={location.pathname} send={(e) => crm.recordOwnerTime(e)}>
    <div className="setup-shell">
      <a className="skip" href="#main">
        Skip to content
      </a>
      <header className="setup-bar">
        <span className="setup-brand">
          <Logo width={120} />
          <span className="wordmark">{tenant?.name ?? "Ogma Hub"}</span>
        </span>
        <span className="setup-bar-actions">
          {location.pathname !== "/setup" && (
            <NavLink className="btn btn-quiet" to="/setup">
              Back to set-up
            </NavLink>
          )}
          <button type="button" className="btn btn-quiet" onClick={() => void client.auth.signOut()}>
            Sign out
          </button>
        </span>
      </header>
      <FoldPageProvider path={location.pathname} hash={location.hash}>
        <main id="main" className="main" tabIndex={-1}>
          <Outlet />
        </main>
      </FoldPageProvider>
    </div>
    </OwnerTimeProvider>
  );
}
